import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { generatePdf } from '../../essential-eight/components/pdf-utils';
import { useTheme } from '@/contexts/ThemeContext';

interface ResultsPdfExportProps {
  children: React.ReactNode;
  filename?: string;
}

const ResultsPdfExport: React.FC<ResultsPdfExportProps> = ({ 
  children, 
  filename = 'privacy-act-assessment.pdf'
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const { mode, setMode } = useTheme();

  const handleExportPdf = async () => {
    if (!contentRef.current || isExporting) return;

    // Store the current theme mode
    const previousMode = mode;
    
    try {
      setIsExporting(true);
      
      // Force light mode for PDF export (avoids oklch parsing issues in html2canvas)
      if (mode !== 'light') {
        setMode('light');
        // Wait for theme to apply
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      await generatePdf(contentRef.current, filename, { download: true, landscape: true });
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      // Restore the previous theme mode
      if (previousMode !== 'light') {
        setMode(previousMode);
      }
      setIsExporting(false);
    }
  };
  
  return (
    <div className="results-pdf-export w-full overflow-x-hidden" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
      <div className="print-container bg-white p-4 rounded-lg w-full max-w-full" ref={contentRef} id="pdf-content" style={{ WebkitOverflowScrolling: 'touch' }}>
        {/* Apply printable styles to children */}
        {children}
      </div>
      
      <div className="flex justify-end mt-4 mr-6 print:hidden">
        <Button
          onClick={handleExportPdf}
          className="print:hidden"
          variant="outline"
          disabled={isExporting}
        >
          <Download className="mr-2 h-4 w-4" />
          {isExporting ? 'Exporting…' : 'Export PDF'}
        </Button>
      </div>
    </div>
  );
};

export default ResultsPdfExport;
